import React, { useState } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { Droppable } from './Droppable';
import { Draggable } from './Draggable';
import Stack from '@mui/system/Stack';
import '../../CSS/DragDropMatching.css';
import RichTextComponents from '../RichTextComponents';
import { Elements } from '@kontent-ai/delivery-sdk';
import ComponentTitle from '../ComponentTitle';

type ItemType = {
  groupId: string;
  id: string;
  value: string;
  isCorrect?: boolean | null; // Track if this specific item is in the correct group
  originalGroupId?: string; // Store original groupId for correctness checking
};

type groupType = {
  id: string;
  title: string;
  items: ItemType[];
};

const DragDropExercise: React.FC<{ element: any }> = ({ element }) => {
  const [isSubmitted, setIsSubmitted] = useState(false);
  // State to track whether an item is being dragged
  const [isDragging, setIsDragging] = useState(false);
  const [groups, setGroups] = useState<groupType[]>(
    element.groups.linkedItems.map((group: any) => ({
      id: group.system.id,
      title: group.elements.title.value,
      items: group.elements.answers.linkedItems
        .filter((item: any) => item && item.elements && item.elements.answer) // Filter out null/undefined items
        .map((item: any) => ({
          groupId: group.system.id,
          id: item.system.id,
          value: item.elements.answer.value,
        })),
    })),
  );

  // State to track the sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );
  // Randomize items on first load, placing all items in a standalone group
  React.useEffect(() => {
    // Get the original structure for validation and correct answers
    const originalGroups = element.groups.linkedItems.map((group: any) => ({
      id: group.system.id,
      title: group.elements.title.value,
      items: group.elements.answers.linkedItems
        .filter((item: any) => item && item.elements && item.elements.answer) // Filter out null/undefined items
        .map((item: any) => ({
          groupId: group.system.id,
          id: item.system.id,
          value: item.elements.answer.value,
        })),
    }));

    // Collect all items from all groups
    const allItems = originalGroups.flatMap((group: groupType) =>
      group.items.map((item: ItemType) => ({
        ...item,
        // Store original groupId for correctness checking
        originalGroupId: item.groupId,
      })),
    );

    // Shuffle all items
    const shuffledAllItems = [...allItems].sort(() => Math.random() - 0.5);

    // Create a standalone group for all items
    const itemsGroup: groupType = {
      id: 'all-items-group',
      title: 'All Items',
      items: shuffledAllItems,
    };

    // Create empty answer groups
    const answerGroups = originalGroups.map((group: groupType) => ({
      ...group,
      items: [] as ItemType[],
    }));

    // Update state with the standalone items group followed by empty answer groups
    setGroups([itemsGroup, ...answerGroups]);
  }, [element]);

  // State to track whether the order is correct
  const [isCorrect, setIsCorrect] = useState<boolean | null>(null);
  // Function to handle the submit action
  const handleSubmit = (): void => {
    // Create a map of correct group assignments from the original data
    const correctItemGroupMap = new Map<string, string>();

    // Collect correct group assignments from original data
    element.groups.linkedItems.forEach((group: any) => {
      const groupId = group.system.id;
      group.elements.answers.linkedItems
        .filter((item: any) => item && item.elements && item.elements.answer) // Filter out null/undefined items
        .forEach((item: any) => {
          // Map item ID to its correct group ID
          correctItemGroupMap.set(item.system.id, groupId);
        });
    });

    // Create updated groups where each item has its correctness marked
    const updatedGroups = groups.map(group => {
      const updatedItems = group.items.map(item => {
        const correctGroupId = correctItemGroupMap.get(item.id);
        // Set item's individual correctness based on whether it's in the right group
        return {
          ...item,
          isCorrect: correctGroupId === group.id,
        };
      });
      return { ...group, items: updatedItems };
    });

    // Update the groups with the correctness information
    setGroups(updatedGroups);

    // Determine if overall exercise is correct (all items in correct groups)
    const allItemsCorrect = updatedGroups.every(group =>
      group.items.every(item => item.isCorrect),
    );

    setIsCorrect(allItemsCorrect);
    setIsSubmitted(true);
  };

  // Function to get the class name for each item based on its individual correctness
  const getItemClassName = (item: ItemType): string => {
    let className = 'draggable-item';
    if (isSubmitted === false || isDragging || item.isCorrect === undefined) {
      className += ' item-default';
    } else if (item.isCorrect) {
      className += ' item-correct';
    } else {
      className += ' item-incorrect';
    }
    return className;
  };

  // Effect to reset correctness when dragging starts
  React.useEffect(() => {
    if (isDragging) {
      // Reset global correctness state
      setIsCorrect(null);

      // Reset individual item correctness for all items
      if (isSubmitted) {
        const resetGroups = groups.map(group => ({
          ...group,
          items: group.items.map(item => ({ ...item, isCorrect: undefined })),
        }));
        setGroups(resetGroups);
        setIsSubmitted(false);
      }
    }
  }, [isDragging, groups, isSubmitted]);

  return (
    <div className="matching-container da-panel-primary">
      {element.title?.value && <ComponentTitle title={element.title.value} />}
      {element.body.value.trim() !== '<p><br></p>' && (
        <div className="dnd-instructions">
          <div className="matching-instructions">
            <RichTextComponents element={element.body} />
          </div>
        </div>
      )}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={(event): void => {
          setIsDragging(false);
          handleDragEnd(event);
        }}
        onDragStart={(): void => setIsDragging(true)}
        onDragCancel={(): void => setIsDragging(false)}
      >
        <div className="matching-exercise-layout">
          {/* Left column: All Items (40% width) */}
          <div className="items-column">
            <div className="group-container">
              <h3 className="group-title">{groups[0].title}</h3>
              <SortableContext
                items={groups[0].items.map(item => item.id)}
                strategy={verticalListSortingStrategy}
              >
                <Stack
                  spacing={2}
                  className="sortable-container"
                  style={{ flexWrap: 'wrap' }}
                >
                  {groups[0].items.length > 0 ? (
                    groups[0].items.map(item => (
                      <Droppable key={item.id} id={item.id}>
                        <Draggable id={item.id}>
                          <div className={getItemClassName(item)}>
                            <RichTextComponents
                              element={
                                {
                                  type: 'rich_text',
                                  value: item.value,
                                } as Elements.RichTextElement
                              }
                            />
                          </div>
                        </Draggable>
                      </Droppable>
                    ))
                  ) : (
                    <Droppable id={groups[0].id}>
                      <div className="empty-droppable"></div>
                    </Droppable>
                  )}
                </Stack>
              </SortableContext>
            </div>
          </div>

          {/* Right column: Answer Groups (60% width) */}
          <div className="groups-column">
            {groups.slice(1).map(group => (
              <div key={group.id} className="group-container">
                <h3 className="group-title">{group.title}</h3>
                <SortableContext
                  items={group.items.map(item => item.id)}
                  strategy={verticalListSortingStrategy}
                >
                  <Stack
                    spacing={2}
                    className="sortable-container"
                    style={{ flexWrap: 'wrap' }}
                  >
                    {group.items.length > 0 ? (
                      group.items.map(item => (
                        <Droppable key={item.id} id={item.id}>
                          <Draggable id={item.id}>
                            <div className={getItemClassName(item)}>
                              <RichTextComponents
                                element={
                                  {
                                    type: 'rich_text',
                                    value: item.value,
                                  } as Elements.RichTextElement
                                }
                              />
                            </div>
                          </Draggable>
                        </Droppable>
                      ))
                    ) : (
                      <Droppable id={group.id}>
                        <div className="empty-droppable"></div>
                      </Droppable>
                    )}
                  </Stack>
                </SortableContext>
              </div>
            ))}
          </div>
        </div>
      </DndContext>

      {/* button to submit the order */}
      <div className="submit-button-container">
        <button onClick={handleSubmit} className="cta-content-button">
          Submit
        </button>
      </div>

      {/* Display feedback based on correctness */}
      {isCorrect !== null && (
        <div
          className={`feedback-message ${
            isCorrect ? 'feedback-correct' : 'feedback-incorrect'
          }`}
        >
          {isCorrect ? 'Items are correct!' : 'Items are incorrect.'}
        </div>
      )}
    </div>
  );

  // Function to handle the drag end event
  function handleDragEnd(event: any): void {
    const { active, over } = event;

    if (active && over && active.id !== over.id) {
      const newGroups = [...groups];
      let activeGroupIndex = -1;
      let activeItemIndex = -1;
      let targetGroupIndex = -1;

      // Find the active item and its group
      for (let i = 0; i < newGroups.length; i++) {
        const itemIndex = newGroups[i].items.findIndex(
          item => item.id === active.id,
        );
        if (itemIndex !== -1) {
          activeGroupIndex = i;
          activeItemIndex = itemIndex;
          break;
        }
      }

      // If we found the active item
      if (activeGroupIndex !== -1 && activeItemIndex !== -1) {
        const activeItem = {
          ...newGroups[activeGroupIndex].items[activeItemIndex],
        };

        // First, check if we're dropping onto another item
        let foundTargetItem = false;
        for (let i = 0; i < newGroups.length; i++) {
          const overItemIndex = newGroups[i].items.findIndex(
            item => item.id === over.id,
          );
          if (overItemIndex !== -1) {
            // We're dropping onto an existing item
            targetGroupIndex = i;
            foundTargetItem = true;

            // Update the groupId of the active item
            activeItem.groupId = newGroups[targetGroupIndex].id;

            // Remove the item from its original group
            newGroups[activeGroupIndex].items.splice(activeItemIndex, 1);

            // Insert the item after the target item
            newGroups[targetGroupIndex].items.splice(
              overItemIndex + 1,
              0,
              activeItem,
            );
            break;
          }
        }

        // If we didn't find a target item, check if we're dropping onto an empty group
        if (!foundTargetItem) {
          // Check if over.id matches any group id (empty group case)
          targetGroupIndex = newGroups.findIndex(group => group.id === over.id);

          if (targetGroupIndex !== -1) {
            // We're dropping onto an empty group
            // Update the groupId of the active item
            activeItem.groupId = newGroups[targetGroupIndex].id;

            // Remove the item from its original group
            newGroups[activeGroupIndex].items.splice(activeItemIndex, 1);

            // Add the item to the beginning of the target group
            newGroups[targetGroupIndex].items.push(activeItem);
          }
        }

        // Update state with the new groups
        setGroups(newGroups);
      }
    }
  }
};

export default DragDropExercise;
